import { v4 as uuidv4 } from 'uuid';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { addTextStyleAction } from 'editor/src/store/design/slice';
import { MediaText, TextStyle } from 'editor/src/store/design/types';
import {
  buildTextStyleElementUpdate,
  getTextStyleFillWrite,
  isTextStyleProtected,
} from 'editor/src/store/design/util/textStyles';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import applyToSelectedMediaTypeOperation from './applyToSelectedMediaTypeOperation';

/**
 * Adds a style to the design, optionally making the selected texts follow it,
 * as one undo step. Texts the user may not edit (isTextStyleProtected) are not assigned. The values normally come from the selected text, whose
 * font is therefore already loaded. Returns the new style's id.
 */
const createTextStyleOperation =
  (name: string, values: Omit<TextStyle, 'id' | 'name'>, assignToSelection: boolean) =>
  (dispatch: ThunkDispatch, getState: () => RootState): string => {
    const style: TextStyle = { id: uuidv4(), name, ...values };
    batch(() => {
      dispatch(saveUndoRedoStateOperation('Text style created'));
      dispatch(addTextStyleAction(style));
      if (assignToSelection) {
        dispatch(
          applyToSelectedMediaTypeOperation(
            'text',
            function assignTextStyle(element: MediaText) {
              // Like assigning: a text locked for the buyer is left as it is.
              return isTextStyleProtected(getState(), element)
                ? undefined
                : buildTextStyleElementUpdate(
                    element,
                    style,
                    getTextStyleFillWrite(
                      getState().design.designData,
                      getState().hostSettings.cmykOnly,
                      style,
                      getState().editor.currentSpreadIndex,
                      element.uuid,
                    ),
                  );
            },
            true,
          ),
        );
      }
    });
    return style.id;
  };

export default createTextStyleOperation;
